import { Box, Container, Grid, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { tokens } from "../../theme";
import { motion } from "framer-motion";
import TopBarOfMarketing from "../../components/TopBarOfMarketing";
import Footer from "../../components/Footer";
import { useTranslation } from "react-i18next";
import Seo from "../../seo/Seo";
import { ORGANIZATION_JSONLD } from "../../seo/seoConfig";
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PeopleIcon from '@mui/icons-material/People';

export default function About({ darkMode, setDarkMode }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('about');
  const { t: tSeo } = useTranslation('seo');

  const ValueCard = ({ icon: Icon, title, description }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      style={{ height: "100%" }}
    >
      <Box
        sx={{
          p: 4,
          borderRadius: "24px",
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          transition: "all 0.3s ease",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)",
            opacity: 0,
            transition: "opacity 0.3s ease",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: "-50%",
            left: "-50%",
            width: "200%",
            height: "200%",
            background: "linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent)",
            transform: "rotate(45deg)",
            transition: "all 0.8s ease",
            opacity: 0,
          },
          "&:hover": {
            transform: "translateY(-10px)",
            "&::before": {
              opacity: 1,
            },
            "&::after": {
              opacity: 1,
              left: "100%",
              top: "100%",
            },
            "& .icon-container": {
              transform: "scale(1.1) rotate(5deg)",
              background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
            },
          },
        }}
      >
        <Box
          className="icon-container"
          sx={{
            width: 70,
            height: 70,
            borderRadius: "20px",
            background: "rgba(255, 255, 255, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
            transition: "all 0.3s ease",
          }}
        >
          <Icon sx={{ fontSize: 48, color: colors.greenAccent[400], transition: "all 0.3s ease" }} />
        </Box>
        <Typography
          variant="h4"
          sx={{
            color: "white",
            fontWeight: 600,
            mb: 2,
            fontFamily: "'Poppins', sans-serif",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: -8,
              left: 0,
              width: 40,
              height: 3,
              background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
              borderRadius: "2px",
            },
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.8)",
            lineHeight: 1.6,
            flex: 1,
          }}
        >
          {description}
        </Typography>
      </Box>
    </motion.div>
  );

  return (
    <Box sx={{ 
      width: "100%",
      background: darkMode ? colors.primary[500] : 'white',
      color: darkMode ? 'white' : colors.grey[100],
    }}>
      <Seo
        path="/about"
        title={tSeo("about.title")}
        description={tSeo("about.description")}
        keywords={tSeo("about.keywords")}
        jsonLd={[ORGANIZATION_JSONLD, {
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: tSeo("about.title"),
          description: tSeo("about.description"),
          url: "https://patientmesh.com/about",
        }]}
      />
      <TopBarOfMarketing darkMode={darkMode} setDarkMode={setDarkMode} />
      
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: "100vh",
          background: darkMode 
            ? `linear-gradient(135deg, ${colors.primary[600]}, ${colors.greenAccent[700]})`
            : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.greenAccent[500]})`,
          pt: "64px",
        }}
      >
        {/* About Us Section */}
        <Container maxWidth="lg" sx={{ py: 12 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h1"
              sx={{
                color: "white",
                fontWeight: 700,
                textAlign: "center",
                mb: 3,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {t('hero.title')}
            </Typography>
            <Typography
              variant="h5"
              component="div"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                textAlign: "center",
                mb: 8,
                maxWidth: "1000px",
                mx: "auto",
                fontWeight: 300,
                lineHeight: 1.8,
                whiteSpace: "pre-line",
              }}
            >
              {t('hero.description')}
            </Typography>
          </motion.div>

          {/* Values Section */}
          <Grid container spacing={4} sx={{ mb: 12 }}>
            <Grid item xs={12} md={6} lg={3}>
              <ValueCard
                icon={MedicalServicesIcon}
                title={t('values.mission.title')}
                description={t('values.mission.description')}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <ValueCard
                icon={VisibilityIcon}
                title={t('values.vision.title')}
                description={t('values.vision.description')}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <ValueCard
                icon={RocketLaunchIcon}
                title={t('values.goals.title')}
                description={t('values.goals.description')}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <ValueCard
                icon={PeopleIcon}
                title={t('values.values.title')}
                description={t('values.values.description')}
              />
            </Grid>
          </Grid>
        </Container>

        <Footer />
      </Box>
    </Box>
  );
} 